import { useEffect } from "react";
import api from "@/lib/api";
import { applyTracking, forceLoadMetaPixel } from "@/lib/tracking";

/**
 * Mounts on every public page. Fetches the merged tracking codes based on
 * (campaign slug, list_id) and applies them once user consent allows.
 * Re-applies when user updates consent via the cookie banner.
 *
 * Also exposes `window.__forceLoadMetaPixel()` so the lead form on the landing
 * page can fire Meta Pixel early (before cookie consent) when the visitor
 * starts interacting with the form.
 */
export default function TrackingLoader({ slug, listId }) {
  useEffect(() => {
    let cancelled = false;
    let codes = null;

    const fetchAndApply = async () => {
      try {
        const params = new URLSearchParams();
        if (slug) params.set("slug", slug);
        if (listId) params.set("list_id", listId);
        const { data } = await api.get(`/tracking/codes?${params.toString()}`);
        if (cancelled) return;
        codes = data;
        // Expose force-load for the lead form (early Meta Pixel fire on form-start)
        window.__forceLoadMetaPixel = () => forceLoadMetaPixel(codes);
        applyTracking(codes);
      } catch (err) {
        console.warn("TrackingLoader: fetch failed", err?.message);
      }
    };

    fetchAndApply();

    // Re-apply on consent updates (banner accept-all, prefs save, programmatic grant)
    const handleUpdate = () => {
      if (codes) applyTracking(codes);
    };
    window.addEventListener("cookie-consent-update", handleUpdate);
    const storageHandler = (e) => {
      if (e.key === "truth_or_debt_cookie_consent") handleUpdate();
    };
    window.addEventListener("storage", storageHandler);

    return () => {
      cancelled = true;
      window.removeEventListener("cookie-consent-update", handleUpdate);
      window.removeEventListener("storage", storageHandler);
      delete window.__forceLoadMetaPixel;
    };
  }, [slug, listId]);

  return null;
}
